import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';

import { Pencil, Trash2 } from '@/components/icons';

import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PILL_TAB } from '../../components/ui/pill-tab';
import { cn } from '../../lib/utils';
import { useDeleteArticle } from './use-article-mutations';

/**
 * 태그 줄 오른쪽 끝의 「✎ 수정」 알약(흰 바탕·검정 테두리)과, 관리자에게만 「삭제」 글자 버튼.
 *
 * 작성자에게는 수정만, 관리자에게는 수정과 삭제. 누가 보나는 화면 편의일 뿐이고
 * 권한은 서버가 본다(403). 삭제는 여전히 확인을 거친다.
 */
export const EDIT_PILL = cn(PILL_TAB, 'gap-1 border-gray-900 bg-white text-gray-900 hover:bg-gray-50');
/** 삭제 — 수정과 같은 알약·같은 자리. 파괴적 동작이라 색만 빨강. */
export const DELETE_PILL = cn(PILL_TAB, 'gap-1 border-danger bg-white text-danger hover:bg-danger-soft disabled:opacity-50');

export function ArticleOwnerActions({ articleId, canDelete }: { articleId: string; canDelete: boolean }) {
  const navigate = useNavigate();
  const del = useDeleteArticle(articleId);
  const [confirming, setConfirming] = useState(false);

  const onConfirmDelete = () => {
    setConfirming(false);
    del.mutate(undefined, {
      onSuccess: () => void navigate({ to: '/articles' }),
    });
  };

  return (
    <div className="ml-auto flex shrink-0 items-center gap-2">
      <Link to="/articles/$id/edit" params={{ id: articleId }} className={EDIT_PILL}>
        <Pencil className="size-3.5" />
        수정
      </Link>
      {canDelete ? (
        <>
          <button type="button" className={DELETE_PILL} onClick={() => setConfirming(true)} disabled={del.isPending}>
            <Trash2 className="size-3.5" />
            삭제
          </button>
        </>
      ) : null}
      {del.isError && (
        <p className="ml-1 text-xs text-danger" role="alert">
          삭제에 실패했어요.
        </p>
      )}

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={onConfirmDelete}
        title="정말로 기사를 삭제할까요?"
        description="되돌릴 수 없어요."
        confirmLabel="예"
        cancelLabel="아니오"
        destructive
        loading={del.isPending}
      />
    </div>
  );
}
